<template>
    <BaseModal :title="title" white v-on="$listeners">
        <div v-if="loadingChangelog" class="changelog-modal">
            {{ $t('loading') }}
        </div>

        <div v-else class="changelog-modal" v-html="changes" />

        <template v-if="allowReload" #footer>
            <div class="l-inline l-spread l-center-v changelog-modal__footer">
                <span>{{ $t('updateHint') }}</span>

                <BaseButton size="small" @click="handleUpdate">
                    {{ $t('update') }}
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import DOMPurify from 'dompurify'
import { marked } from 'marked'

import BaseButton from './BaseButton'
import BaseModal from './BaseModal'

const signatureMap = {
    mobatime: 'Mobatime',
    axroad: 'adnexo',
}

export default {
    name: 'ChangelogModal',
    components: {
        BaseButton,
        BaseModal,
    },
    props: {
        allowReload: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            changelog: null,
            loadingChangelog: false,
        }
    },
    computed: {
        changes() {
            if (!this.changelog) {
                return ''
            }

            const replacedMdContent = this.changelog
                .replaceAll(
                    '${instance}',
                    signatureMap[process.env.VUE_APP_INSTANCE] ||
                        signatureMap.axroad
                )
                .replaceAll(
                    '${mail}',
                    process.env.VUE_APP_SUPPORT_EMAIL || 'support@adnexo.ch'
                )
            return DOMPurify.sanitize(marked(replacedMdContent))
        },
        title() {
            return `${this.$t('changelogTitle')}: ${
                process.env.VUE_APP_VERSION
            }`
        },
    },
    async mounted() {
        this.loadingChangelog = true
        const lang = this.$i18n.locale || 'de'

        try {
            this.changelog = await this.fetchChangelog(lang)
        } catch (error) {
            if (this.allowReload) {
                this.handleUpdate()
            }
        } finally {
            this.loadingChangelog = false
        }
    },
    methods: {
        async fetchChangelog(lang) {
            const subDirPath = process.env.VUE_APP_SUB_DIR_PATH
            const rootPath = subDirPath ? `/${subDirPath}` : ''
            const changelogUrl = `${rootPath}/changelogs/user_changelog_${lang}.md?v=${encodeURIComponent(
                process.env.VUE_APP_VERSION
            )}`

            const response = await fetch(changelogUrl, {
                cache: 'no-store',
            })

            if (!response.ok) {
                throw new Error(`Failed to load changelog: ${response.status}`)
            }

            return response.text()
        },
        handleUpdate() {
            window.location.reload()
        },
    },
}
</script>

<i18n>
{
    "en": {
        "changelogTitle": "Updates for Version",
        "update": "Reload",
        "updateHint": "Reload the page to get the latest version",
        "loading": "Loading updates..."
    },
    "de": {
        "changelogTitle": "Updates für Version",
        "update": "Neu laden",
        "updateHint": "Laden Sie die Seite neu, um die neueste Version zu erhalten",
        "loading": "Updates werden geladen..."
    },
    "fr": {
        "changelogTitle": "Mises à jour pour la version",
        "update": "Recharger",
        "loading": "Chargement des mises a jour...",
        "updateHint": "Recharger la page pour obtenir la dernière version"
    },
    "it": {
        "changelogTitle": "Aggiornamenti per la versione",
        "update": "Ricarica",
        "updateHint": "Ricarica la pagina per ottenere l'ultima versione",
        "loading": "Caricamento degli aggiornamenti..."
    }
}
</i18n>

<style lang="scss">
.changelog-modal {
    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin-bottom: 16px;
    }
    li {
        margin-bottom: 8px;
    }
}
</style>
